import * as Dfns from "date-fns/fp";
import * as R from "ramda";
import {
  BpmDateString,
  CompanyFiscalCal,
  FiscalCalType,
  FiscalPatternCal,
  getMonthLabelPattern,
  getQuarterLabelPattern,
  getQuarterLabelYearly,
  getStartOfFiscalMonth,
  getStartOfFiscalQuarter,
  getStartOfFiscalYear,
  getYearLabelPattern,
  getYearLabelYearly
} from "@blisspointmedia/bpm-types/dist/FiscalCalendar";
import {
  DateGroupingKey,
  DAY_DATE_LABEL,
  DAY_DATE_TOOLTIP_LABEL,
  MONTH_DATE_LABEL,
  MONTH_DATE_TOOLTIP_LABEL,
  QUARTER_DATE_LABEL,
  WEEK_DATE_LABEL,
  YEAR_DATE_LABEL
} from "../TVADCrossChannel/homePageConstants";
import { DateRange } from "../utils/types";

export const SHORT_DATE_FORMAT = "MM/dd";
const DATE_FORMAT = "yyyy-MM-dd";

/**
 * Splits a given date range into an array of formatted date strings representing the start of each week within the range.
 * The range is inclusive of both the start and end dates.
 *
 * @param {DateRange} dateRange - The range of dates to be split into weeks.
 * @returns {string[]} An array of formatted date strings, each representing the start of a week within the date range.
 */
export const getWeeksFromRange = (dateRange: DateRange): string[] => {
  let weeks: string[] = [];
  let currentWeek = dateRange.start;
  while (currentWeek <= dateRange.end) {
    weeks.push(currentWeek);
    currentWeek = R.pipe(Dfns.parseISO, Dfns.addWeeks(1), Dfns.format(DATE_FORMAT))(currentWeek);
  }

  return weeks;
};

/**
 * Checks if the provided date is a valid Date object.
 *
 * @param {Date} date - The date to validate.
 * @returns {boolean} If it's a valid date or not
 */
export const validDate = (date: Date): boolean => {
  if (R.isNil(date)) {
    return false;
  }
  const timeNow = new Date(date);
  return !R.isNil(timeNow) && !R.isNil(timeNow.getDate()) && !isNaN(timeNow.getDate());
};

/**
 * Returns a formatted date string representing the date exactly N days prior to today.
 *
 * @param {number} [daysAgo=1] - The number of days ago to subtract from the current date (optional, defaults to 1).
 * @param {string} [format=DATE_FORMAT] - The format in which to return the date (optional, defaults to the predefined DATE_FORMAT).
 * @returns {string} The formatted date string of the specified days ago.
 */
export const getFormattedDaysAgo = (daysAgo = 1, format = DATE_FORMAT): string =>
  R.pipe(Dfns.subDays(daysAgo), Dfns.format(format))(new Date());

/**
 * Returns a formatted date string representing the date exactly N months prior to today.
 *
 * @param {number} [monthsAgo=1] - The number of months ago to subtract from the current date (optional, defaults to 1).
 * @param {string} [format=DATE_FORMAT] - The format in which to return the date (optional, defaults to the predefined DATE_FORMAT).
 * @returns {string} The formatted date string of the specified months ago.
 */
export const getFormattedMonthsAgo = (monthsAgo = 1, format = DATE_FORMAT): string =>
  R.pipe(Dfns.subMonths(monthsAgo), Dfns.format(format))(new Date());

/**
 * A refactor of the original function from homepage utils that incorporates fiscal calendars
 * Returns a formatted date string representing the date grouping.
 * The "beginning" of a month/year/quarter could be a date other than the first because of fiscal calendars
 *
 * @param {string} date The date to be grouped.
 * @param {DateGroupingKey} groupingKey Grouping key, determines the interval of the group.
 * @param {CompanyFiscalCal} fiscalCal Company based fiscal cal from company info, if an empty object default to traditional calendar with week start on Monday
 * @returns {string} The formatted date string of the specified group.
 */
export const getGroupingValue = (
  date: string,
  groupingKey: DateGroupingKey,
  fiscalCal: CompanyFiscalCal
): any => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date)) || !groupingKey) {
    return "";
  }
  const { startOfWeek } = fiscalCal || {};
  // this defaults to monday
  const getStartOfFiscalWeek = Dfns.startOfWeekWithOptions({
    weekStartsOn: R.isNil(startOfWeek) ? 1 : startOfWeek,
  });

  switch (groupingKey) {
    case "Day":
      return date;
    case "Week":
      return Dfns.format(DATE_FORMAT, getStartOfFiscalWeek(Dfns.parseISO(date)));
    case "Month":
      return Dfns.format(DATE_FORMAT, getStartOfFiscalMonth(date, fiscalCal));
    case "Quarter":
      return Dfns.format(DATE_FORMAT, getStartOfFiscalQuarter(date, fiscalCal));
    case "Year":
      return Dfns.format(DATE_FORMAT, getStartOfFiscalYear(date, fiscalCal));
    default:
      throw new Error(`Invalid grouping key: ${groupingKey}`);
  }
};

export const formatDateLabel = (
  date: string,
  fiscalCal: CompanyFiscalCal,
  groupingKey = "Day",
  isTooltip = false
): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return "";
  }
  const { calType, seedDate, generatedPatternCal } = fiscalCal || {};

  const parsedDate = Dfns.parseISO(date);

  let dateToUse = parsedDate;

  let format = "";
  switch (groupingKey) {
    case "Day":
      format = isTooltip ? DAY_DATE_TOOLTIP_LABEL : DAY_DATE_LABEL;
      break;
    case "Week":
      format = isTooltip ? DAY_DATE_TOOLTIP_LABEL : WEEK_DATE_LABEL;
      break;
    case "Month":
      switch (calType) {
        case FiscalCalType.PATTERN: {
          dateToUse = getMonthLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
      }
      format = isTooltip ? MONTH_DATE_TOOLTIP_LABEL : MONTH_DATE_LABEL;
      break;
    case "Quarter": {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getQuarterLabelYearly(date, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getQuarterLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
      }
      format = QUARTER_DATE_LABEL;
      break;
    }
    case "Year": {
      switch (calType) {
        case FiscalCalType.YEARLY: {
          dateToUse = getYearLabelYearly(date, seedDate as string);
          break;
        }
        case FiscalCalType.PATTERN: {
          dateToUse = getYearLabelPattern(date, generatedPatternCal as FiscalPatternCal);
          break;
        }
      }
      format = YEAR_DATE_LABEL;
      break;
    }
    default:
      format = DAY_DATE_LABEL;
  }

  const formattedDate = Dfns.format(format, dateToUse);

  if (groupingKey === "Week") {
    return `w/o ${formattedDate}`;
  }

  return formattedDate;
};

export const dateToMddyy = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return "";
  }
  return Dfns.format("M/dd/yy", Dfns.parseISO(date));
};

export const parseEpochDate = (dateNum: number): BpmDateString => {
  const dateObj = new Date(dateNum);
  return Dfns.format("yyyy-MM-dd", dateObj) as BpmDateString;
};

export const dateToMddyyDashed = (date: string): string => {
  if (!date || !Dfns.isValid(Dfns.parseISO(date))) {
    return "";
  }
  return Dfns.format("M-dd-yy", Dfns.parseISO(date));
};
